import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';

import { useFocusEffect } from '@react-navigation/native';
import {
  FlatList,
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import {
  ActivityIndicator,
  Button as PaperButton,
  IconButton,
  Menu,
  SegmentedButtons,
} from 'react-native-paper';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { GTFonts, useLayoutSize, useTheme } from '../../theme';
import {
  fs,
  getCurrencyDisplayFormat,
  iconSize,
  normalizeEmail,
  s,
  v,
} from '../../utils/libs';
import { getApiMessage } from '../../utils/apiError';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import WebHeaderBar from '../../components/web/WebHeaderBar';
import useWebHeader from '../../components/web/useWebHeader';
import KeyboardAwareFormScroll from '../../components/Reusables/KeyboardAwareFormScroll';
import HeaderIconButton from '../../components/Reusables/headers/HeaderIconButton';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import {
  getSupplierInformation,
  getPurchaseOrdersInformation,
  getSupplierInvoicesInformation,
  parseSupplierResponse,
  parsePurchaseOrdersResponse,
  parseInvoicesResponse,
  updateSupplierInformation,
} from '../../api/backofficeSuppliers';
import {
  setSupplierByIdData,
  setSupplierByIdError,
  setSupplierByIdLoading,
  setPurchaseOrdersBySupplierIdData,
  setPurchaseOrdersBySupplierIdError,
  setPurchaseOrdersBySupplierIdLoading,
  setInvoicesBySupplierIdData,
  setInvoicesBySupplierIdError,
  setInvoicesBySupplierIdLoading,
  upsertSupplierInList,
} from '../../redux/backoffice/suppliersSlice';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import { AppAlert } from '../../services/AppAlert';
import { AppSnackbar } from '../../services/AppSnackbar';
import type { RootStackParamList } from '../../navigation/AppStack';

type RouteParams = {
  supplierId: string;
};

const BackOfficeSupplierDetailsScreen: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const route = useRoute();
  const { supplierId } = (route.params || {}) as RouteParams;
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { useSidebarNav } = useLayoutSize();
  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();

  const supplierEntity = useAppSelector(
    state => state.backofficeSuppliers.supplierById[String(supplierId || '')],
  );
  const poEntity = useAppSelector(
    state =>
      state.backofficeSuppliers.purchaseOrdersBySupplierId[
      String(supplierId || '')
      ],
  );
  const invoiceEntity = useAppSelector(
    state =>
      state.backofficeSuppliers.invoicesBySupplierId[String(supplierId || '')],
  );

  const supplier = supplierEntity?.data || null;
  const purchaseOrders = Array.isArray(poEntity?.data) ? poEntity.data : [];
  const invoices = Array.isArray(invoiceEntity?.data) ? invoiceEntity.data : [];
  const purchaseOrdersLoading =
    Boolean(poEntity?.loading) && purchaseOrders.length === 0;
  const invoicesLoading =
    Boolean(invoiceEntity?.loading) && invoices.length === 0;
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => getCurrencyDisplayFormat(currency),
    [currency],
  );

  const [activeTab, setActiveTab] = useState<
    'purchaseOrders' | 'invoices' | 'details'
  >('purchaseOrders');
  const [saving, setSaving] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const normalizedPhone = useMemo(
    () => String(supplier?.bp_supplier_phone_number || '').replace(/[^\d+]/g, ''),
    [supplier?.bp_supplier_phone_number],
  );
  const normalizedWhatsapp = useMemo(
    () =>
      String(supplier?.bp_supplier_whatsapp_number || '')
        .replace(/[^\d+]/g, '')
        .replace(/^\+/, ''),
    [supplier?.bp_supplier_whatsapp_number],
  );
  const normalizedSupplierEmail = useMemo(
    () => normalizeEmail(String(supplier?.bp_supplier_email_address || '')),
    [supplier?.bp_supplier_email_address],
  );

  const openPhoneNumber = useCallback((phone?: string) => {
    const normalized = String(phone || '').replace(/[^\d+]/g, '');
    if (!normalized) return;
    Linking.openURL(`tel:${normalized}`).catch(() => {
      AppSnackbar.show('Unable to open phone app.', 'error');
    });
  }, []);

  const openWhatsApp = useCallback((phone?: string) => {
    const normalized = String(phone || '')
      .replace(/[^\d+]/g, '')
      .replace(/^\+/, '');
    if (!normalized) return;
    Linking.openURL(`https://wa.me/${normalized}`).catch(() => {
      AppSnackbar.show('Unable to open WhatsApp.', 'error');
    });
  }, []);

  const openEmail = useCallback((email?: string) => {
    const normalized = normalizeEmail(String(email || ''));
    if (!normalized) return;
    Linking.openURL(`mailto:${normalized}`).catch(() => {
      AppSnackbar.show('Unable to open email client.', 'error');
    });
  }, []);

  const getPOStatusColor = useCallback(
    (rawStatus: string) => {
      const status = String(rawStatus || 'draft')
        .trim()
        .toLowerCase();
      if (status === 'sent') return theme.colors.infoDark;
      if (status === 'partially_fulfilled' || status === 'partial')
        return theme.colors.warningDark;
      if (status === 'fulfilled') return theme.colors.successEmerald;
      if (status === 'cancelled' || status === 'canceled')
        return theme.colors.errorDark;
      return theme.colors.textGray700 || theme.colors.textSecondary;
    },
    [theme.colors],
  );

  const fetchSupplier = useCallback(async () => {
    if (!supplierId || !owner?.accessToken) return;
    dispatch(setSupplierByIdLoading(supplierId));
    try {
      const res = await getSupplierInformation(
        axiosBackoffice as any,
        supplierId,
        { accessToken: owner.accessToken },
      );
      const parsed = parseSupplierResponse(res);
      if (!parsed?.success || !parsed.supplier) {
        dispatch(
          setSupplierByIdError({
            id: supplierId,
            error: parsed?.message || 'Failed to load supplier',
          }),
        );
        return;
      }
      dispatch(setSupplierByIdData({ id: supplierId, data: parsed.supplier }));
      dispatch(upsertSupplierInList(parsed.supplier));
    } catch (error: any) {
      dispatch(
        setSupplierByIdError({
          id: supplierId,
          error: getApiMessage(error, 'Failed to load supplier'),
        }),
      );
    }
  }, [axiosBackoffice, dispatch, owner?.accessToken, supplierId]);

  const fetchPurchaseOrders = useCallback(async () => {
    if (!supplierId || !owner?.accessToken) return;
    dispatch(setPurchaseOrdersBySupplierIdLoading(supplierId));
    try {
      const res = await getPurchaseOrdersInformation(
        axiosBackoffice as any,
        { supplierId },
        { accessToken: owner.accessToken },
      );
      const parsed = parsePurchaseOrdersResponse(res);
      if (!parsed?.success) {
        dispatch(
          setPurchaseOrdersBySupplierIdError({
            id: supplierId,
            error: parsed?.message || 'Failed to load purchase orders',
          }),
        );
        return;
      }
      dispatch(
        setPurchaseOrdersBySupplierIdData({
          id: supplierId,
          data: parsed.purchaseOrders || [],
        }),
      );
    } catch (error: any) {
      dispatch(
        setPurchaseOrdersBySupplierIdError({
          id: supplierId,
          error: getApiMessage(error, 'Failed to load purchase orders'),
        }),
      );
    }
  }, [axiosBackoffice, dispatch, owner?.accessToken, supplierId]);

  const fetchInvoices = useCallback(async () => {
    if (!supplierId || !owner?.accessToken) return;
    dispatch(setInvoicesBySupplierIdLoading(supplierId));
    try {
      const res = await getSupplierInvoicesInformation(
        axiosBackoffice as any,
        { supplierId },
        { accessToken: owner.accessToken },
      );
      const parsed = parseInvoicesResponse(res);
      if (!parsed?.success) {
        dispatch(
          setInvoicesBySupplierIdError({
            id: supplierId,
            error: parsed?.message || 'Failed to load invoices',
          }),
        );
        return;
      }
      dispatch(
        setInvoicesBySupplierIdData({
          id: supplierId,
          data: parsed.invoices || [],
        }),
      );
    } catch (error: any) {
      dispatch(
        setInvoicesBySupplierIdError({
          id: supplierId,
          error: getApiMessage(error, 'Failed to load invoices'),
        }),
      );
    }
  }, [axiosBackoffice, dispatch, owner?.accessToken, supplierId]);

  useEffect(() => {
    if (!owner?.accessToken || !supplierId) return;
    fetchSupplier();
  }, [
    owner?.accessToken,
  ]);

  useFocusEffect(
    useCallback(() => {
      if (!owner?.accessToken || !supplierId) return;
      fetchInvoices();
      fetchPurchaseOrders();
    }, [fetchInvoices, fetchPurchaseOrders, owner?.accessToken, supplierId]),
  );

  const onDeactivateSupplier = useCallback(async () => {
    if (!supplierId || !owner?.accessToken || !supplier) return;
    setSaving(true);
    try {
      const payload = {
        bp_supplier_name: String(supplier.bp_supplier_name || ''),
        bp_supplier_contact_person_name:
          String(supplier.bp_supplier_contact_person_name || '') || undefined,
        bp_supplier_phone_number:
          String(supplier.bp_supplier_phone_number || '') || undefined,
        bp_supplier_whatsapp_number:
          String(supplier.bp_supplier_whatsapp_number || '') || undefined,
        bp_supplier_email_address:
          normalizeEmail(supplier.bp_supplier_email_address || '') || undefined,
        bp_supplier_physical_address:
          String(supplier.bp_supplier_physical_address || '') || undefined,
        bp_supplier_tax_vat_number:
          String(supplier.bp_supplier_tax_vat_number || '') || undefined,
        bp_supplier_preferred_communication_channel: String(
          supplier.bp_supplier_preferred_communication_channel || 'any',
        ),
        bp_supplier_notes:
          String(supplier.bp_supplier_notes || '') || undefined,
        bp_supplier_status: 'inactive',
      };

      const res = await updateSupplierInformation(
        axiosBackoffice as any,
        supplierId,
        payload,
        { accessToken: owner.accessToken },
      );
      const ok = res?.data?.success === true || res?.data?.status === 200;
      if (!ok) {
        AppSnackbar.show(
          String(res?.data?.message || 'Failed to deactivate supplier'),
          'error',
        );
        return;
      }
      const parsed = parseSupplierResponse(res);
      const updated =
        parsed?.supplier || res?.data?.supplier || res?.data?.data;
      if (updated?._id) {
        dispatch(setSupplierByIdData({ id: supplierId, data: updated }));
        dispatch(upsertSupplierInList(updated));
      }
      AppSnackbar.show(
        String(res?.data?.message || 'Supplier deactivated successfully'),
        'success',
      );
    } catch (error: any) {
      AppSnackbar.show(
        getApiMessage(error, 'Failed to deactivate supplier'),
        'error',
      );
    } finally {
      setSaving(false);
    }
  }, [axiosBackoffice, dispatch, owner?.accessToken, supplier, supplierId]);

  const headerRightNode = useMemo(
    () => (
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Menu
          visible={menuVisible}
          onDismiss={() => setMenuVisible(false)}
          anchor={
            <HeaderIconButton
              tooltip="Open supplier actions"
              onPress={() => setMenuVisible(true)}
              style={{ padding: s(6) }}
              accessibilityLabel="Open supplier actions">
              <MaterialCommunityIcons
                name="dots-vertical"
                size={iconSize(20)}
                color={theme.colors.textInverse}
              />
            </HeaderIconButton>
          }
          anchorPosition="bottom"
          contentStyle={{ backgroundColor: theme.colors.surface }}>
          <Menu.Item
            leadingIcon="pencil"
            theme={{ colors: { primary: theme.colors.primaryDark } }}
            onPress={() => {
              setMenuVisible(false);
              navigation.navigate('BackOfficeAddSupplier', {
                supplierId: String(supplierId),
              });
            }}
            disabled={!supplier}
            title="Edit"
            titleStyle={{ color: theme.colors.primaryDark }}
          />
          {supplier &&
            !['inactive', 'deactivated'].includes(
              String(supplier.bp_supplier_status || '').toLowerCase(),
            ) && (
              <Menu.Item
                leadingIcon="account-off"
                disabled={saving}
                theme={{ colors: { primary: theme.colors.warningDark } }}
                onPress={async () => {
                  setMenuVisible(false);
                  if (!supplier) return;
                  const confirmed = await AppAlert.confirm(
                    'Deactivate Supplier',
                    `Are you sure you want to deactivate ${String(supplier?.bp_supplier_name || 'this supplier')}?`,
                    {
                      okText: 'Deactivate',
                      cancelText: 'Cancel',
                      cancelable: true,
                    },
                  );
                  if (confirmed) onDeactivateSupplier();
                }}
                title="Deactivate"
                titleStyle={{ color: theme.colors.warningDark }}
              />
            )}
        </Menu>
      </View>
    ),
    [
      menuVisible,
      navigation,
      onDeactivateSupplier,
      saving,
      supplier,
      supplierId,
      theme.colors.primaryDark,
      theme.colors.surface,
      theme.colors.warningDark,
    ],
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => headerRightNode,
    });
  }, [headerRightNode, navigation]);

  const webHeaderNode = useMemo(() => {
    if (!useSidebarNav) return null;
    return (
      <WebHeaderBar
        title="Supplier Details"
        titleAlign="center"
        rightSlot={headerRightNode}
        onBack={() => {
          if (navigation.canGoBack()) {
            navigation.goBack();
            return;
          }
          navigation.navigate('BackOffice', { screen: 'Suppliers' } as never);
        }}
      />
    );
  }, [headerRightNode, navigation, useSidebarNav]);

  useWebHeader(webHeaderNode, Boolean(webHeaderNode));

  const renderPurchaseOrder = useCallback(
    ({ item }: { item: any }) => {
      const number = String(
        item?.bp_purchase_order_number ||
        `Purchase order ${item?._id?.slice(-6) || ''}`,
      );
      const status = String(item?.bp_purchase_order_status || 'draft');
      const statusColor = getPOStatusColor(status);
      return (
        <Pressable
          onPress={() =>
            navigation.navigate('BackOfficePurchaseOrderDetails', {
              supplierId: String(supplierId),
              purchaseOrderId: String(item?._id || ''),
            })
          }
          style={({ pressed, hovered }: any) => [
            styles.listItemCard,
            pressed && styles.listItemPressed,
            Platform.OS === 'web' && hovered && styles.listItemHovered,
          ]}>
          <View style={styles.listItemContent}>
            <Text style={styles.listItemTitle}>{number}</Text>
            <Text style={styles.listItemMeta}>
              Status:{' '}
              <Text style={[styles.listItemMeta, { color: statusColor }]}>
                {status}
              </Text>
            </Text>
            {!!item?.bp_purchase_order_expected_delivery_date && (
              <Text style={styles.listItemMeta}>
                Expected:{' '}
                {String(item.bp_purchase_order_expected_delivery_date).slice(
                  0,
                  10,
                )}
              </Text>
            )}
          </View>
          <MaterialCommunityIcons
            name="chevron-right"
            size={iconSize(24)}
            color={theme.colors.textSecondary}
            style={styles.listItemChevron}
          />
        </Pressable>
      );
    },
    [getPOStatusColor, navigation, styles, supplierId, theme.colors.textSecondary],
  );

  const renderInvoice = useCallback(
    ({ item }: { item: any }) => {
      const number = String(
        item?.bp_supplier_invoice_number || `INV ${item?._id?.slice(-6) || ''}`,
      );
      const total = Number(item?.bp_supplier_invoice_total_amount || 0);
      return (
        <View style={styles.listItemCard}>
          <Text style={styles.listItemTitle}>{number}</Text>
          <Text style={styles.listItemMeta}>
            Amount: {currencyText} {total.toLocaleString()}
          </Text>
          {!!item?.bp_supplier_invoice_date && (
            <Text style={styles.listItemMeta}>
              Date: {String(item.bp_supplier_invoice_date).slice(0, 10)}
            </Text>
          )}
        </View>
      );
    },
    [currencyText, styles],
  );

  return (
    <KeyboardAwareFormScroll
      style={styles.container}
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps="handled"
      // baseBottomPadding={v(20)}
      bottomOffset={v(240)}>
      <Text style={styles.subtitle}>
        Manage supplier profile, purchase orders, and invoice history.
      </Text>
      <View style={styles.tabbedCard}>
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>
            {String(supplier?.bp_supplier_name || 'Supplier Details')}
          </Text>
        </View>
        <SegmentedButtons
          value={activeTab}
          onValueChange={value => setActiveTab(value as any)}
          buttons={[
            {
              value: 'purchaseOrders',
              label: `Purchase orders (${purchaseOrders.length})`,
              style: styles.segmentedButtonFirst,
            },
            {
              value: 'invoices',
              label: `Invoices (${invoices.length})`,
              style: styles.segmentedButtonInvoices,
            },
            {
              value: 'details',
              label: 'Details',
              style: styles.segmentedButtonDetails,
            },
          ]}
          style={styles.segmented}
          theme={{
            colors: {
              secondaryContainer: theme.colors.ActiveSegmentedButton,
              onSecondaryContainer: theme.colors.textGray900,
              outline: theme.colors.border,
            },
          }}
        />
        <View style={styles.tabInfoRow}>
          <MaterialCommunityIcons
            name={
              activeTab === 'purchaseOrders'
                ? 'clipboard-list-outline'
                : activeTab === 'invoices'
                  ? 'file-document-outline'
                  : 'account-box-outline'
            }
            size={iconSize(16)}
            color={theme.colors.textSecondary}
          />
          <Text style={styles.tabInfoText}>
            {activeTab === 'purchaseOrders'
              ? "Orders you've placed with this supplier."
              : activeTab === 'invoices'
                ? "What they've billed you so far."
                : 'Contact info and supplier profile.'}
          </Text>
        </View>
        {activeTab === 'purchaseOrders' && (
          <View style={styles.tabContent}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Purchase Orders</Text>
              <PaperButton
                mode="contained"
                uppercase={false}
                icon="file-document-outline"
                onPress={() =>
                  navigation.navigate('BackOfficePurchaseOrderUpsert', {
                    supplierId: String(supplierId),
                  })
                }
                buttonColor={theme.colors.successDark}
                style={styles.tableActionBtn}
                contentStyle={styles.tableActionBtnContent}
                labelStyle={styles.tableActionBtnLabel}>
                New Purchase Order
              </PaperButton>
            </View>
            <FlatList
              data={purchaseOrders}
              keyExtractor={item => String(item?._id)}
              renderItem={renderPurchaseOrder}
              scrollEnabled={false}
              ItemSeparatorComponent={() => (
                <View style={styles.listItemSeparator} />
              )}
              ListEmptyComponent={
                purchaseOrdersLoading ? (
                  <View style={styles.inlineLoadingWrap}>
                    <ActivityIndicator size="small" />
                    <Text style={styles.emptyLabel}>
                      Loading purchase orders...
                    </Text>
                  </View>
                ) : (
                  <Text style={styles.emptyLabel}>
                    No purchase orders found.
                  </Text>
                )
              }
            />
          </View>
        )}
        {activeTab === 'invoices' && (
          <View style={styles.tabContent}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Invoices</Text>
              <PaperButton
                mode="contained"
                uppercase={false}
                icon="plus"
                onPress={() =>
                  navigation.navigate('BackOfficeAddInvoice', {
                    supplierId: String(supplierId),
                  })
                }
                buttonColor={theme.colors.successDark}
                style={styles.tableActionBtn}
                contentStyle={styles.tableActionBtnContent}
                labelStyle={styles.tableActionBtnLabel}>
                Add Invoice
              </PaperButton>
            </View>
            <FlatList
              data={invoices}
              keyExtractor={item => String(item?._id)}
              renderItem={renderInvoice}
              scrollEnabled={false}
              ItemSeparatorComponent={() => (
                <View style={styles.listItemSeparator} />
              )}
              ListEmptyComponent={
                invoicesLoading ? (
                  <View style={styles.inlineLoadingWrap}>
                    <ActivityIndicator size="small" />
                    <Text style={styles.emptyLabel}>Loading invoices...</Text>
                  </View>
                ) : (
                  <Text style={styles.emptyLabel}>No invoices found.</Text>
                )
              }
            />
          </View>
        )}
        {activeTab === 'details' && (
          <View style={styles.tabContent}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Supplier Details</Text>
              <PaperButton
                mode="contained"
                uppercase={false}
                icon="pencil"
                onPress={() =>
                  navigation.navigate('BackOfficeAddSupplier', {
                    supplierId: String(supplierId),
                  })
                }
                style={styles.tableActionBtn}
                contentStyle={styles.tableActionBtnContent}
                labelStyle={styles.tableActionBtnLabel}
                disabled={!supplier}>
                Edit
              </PaperButton>
            </View>
            {supplier ? (
              <View style={styles.detailsGrid}>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>Contact Person</Text>
                  <Text style={styles.detailValue}>
                    {String(supplier.bp_supplier_contact_person_name || '—')}
                  </Text>
                </View>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>Phone</Text>
                  <Pressable
                    disabled={!normalizedPhone}
                    accessibilityRole={normalizedPhone ? 'link' : undefined}
                    onPress={() =>
                      openPhoneNumber(String(supplier.bp_supplier_phone_number || ''))
                    }>
                    <Text
                      style={[
                        styles.detailValue,
                        normalizedPhone && styles.detailLinkValue,
                      ]}>
                      {String(supplier.bp_supplier_phone_number || '—')}
                    </Text>
                  </Pressable>
                </View>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>WhatsApp</Text>
                  <Pressable
                    disabled={!normalizedWhatsapp}
                    accessibilityRole={normalizedWhatsapp ? 'link' : undefined}
                    onPress={() =>
                      openWhatsApp(String(supplier.bp_supplier_whatsapp_number || ''))
                    }>
                    <Text
                      style={[
                        styles.detailValue,
                        normalizedWhatsapp && styles.detailLinkValue,
                      ]}>
                      {String(supplier.bp_supplier_whatsapp_number || '—')}
                    </Text>
                  </Pressable>
                </View>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>Email</Text>
                  <Pressable
                    disabled={!normalizedSupplierEmail}
                    accessibilityRole={normalizedSupplierEmail ? 'link' : undefined}
                    onPress={() =>
                      openEmail(String(supplier.bp_supplier_email_address || ''))
                    }>
                    <Text
                      style={[
                        styles.detailValue,
                        normalizedSupplierEmail && styles.detailLinkValue,
                      ]}>
                      {String(supplier.bp_supplier_email_address || '—')}
                    </Text>
                  </Pressable>
                </View>
                <View style={styles.detailItemFull}>
                  <Text style={styles.detailLabel}>Address</Text>
                  <Text style={styles.detailValue}>
                    {String(supplier.bp_supplier_physical_address || '—')}
                  </Text>
                </View>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>Tax / VAT</Text>
                  <Text style={styles.detailValue}>
                    {String(supplier.bp_supplier_tax_vat_number || '—')}
                  </Text>
                </View>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>Preferred Channel</Text>
                  <Text style={styles.detailValue}>
                    {String(
                      (() => {
                        const c =
                          supplier.bp_supplier_preferred_communication_channel ||
                          '';
                        if (!c) return '—';
                        return c === 'phone' ? 'sms' : c;
                      })(),
                    )}
                  </Text>
                </View>
                {!!supplier.bp_supplier_notes && (
                  <View style={styles.detailItemFull}>
                    <Text style={styles.detailLabel}>Notes</Text>
                    <Text style={styles.detailValue}>
                      {String(supplier.bp_supplier_notes)}
                    </Text>
                  </View>
                )}
              </View>
            ) : (
              <Text style={styles.emptyLabel}>Loading supplier details...</Text>
            )}
          </View>
        )}
      </View>
    </KeyboardAwareFormScroll>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
      }),
    },
    content: {
      padding: s(12),
      gap: v(10),
      paddingBottom: v(24),
      ...(Platform.OS === 'web' && {
        padding: s(24),
      }),
    },
    cardHeader: {
      marginBottom: v(12),
    },
    cardTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(18),
      lineHeight: fs(22),
      textAlign: 'center',
      color: theme.colors.text,
      marginBottom: v(12),
    },
    title: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(18),
      lineHeight: fs(22),
      color: theme.colors.text,
    },
    subtitle: {
      marginTop: v(4),
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    infoRow: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    tabbedCard: {
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      padding: s(12),
      paddingTop: s(10),
      ...theme.shadows.sm,
    },
    segmented: {
      backgroundColor: theme.colors.surfaceSecondary,
      marginBottom: v(8),
    },
    segmentedButtonFirst: {
      flex: 2.5,
      minWidth: s(100),
    },
    segmentedButtonInvoices: {
      flex: 1.75,
    },
    segmentedButtonDetails: {
      flex: 0.5,
    },
    tabInfoRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6),
      paddingVertical: v(8),
      paddingHorizontal: s(4),
    },
    tabInfoText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      flex: 1,
    },
    tabContent: {
      gap: v(8),
    },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: v(16),
    },
    tableActionBtn: {
      borderRadius: theme.borderRadius.md,
      minWidth: s(96),
      elevation: 0,
    },
    tableActionBtnContent: {
      height: v(36),
      // paddingHorizontal: s(10),
    },
    tableActionBtnLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
      letterSpacing: 0.2,
    },
    sectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(15),
      lineHeight: fs(19),
      color: theme.colors.text,
    },
    listItemCard: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: theme.borderRadius.md,
      padding: s(10),
      ...(Platform.OS === 'web' && {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease-out',
      }),
    } as any,
    listItemChevron: {
      marginLeft: s(8),
    },
    listItemContent: {
      flex: 1,
      gap: v(4),
    },
    listItemSeparator: {
      height: v(12),
    },
    inlineLoadingWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: v(24),
      gap: v(8),
    },
    listItemHovered: {
      backgroundColor: theme.colors.surfaceSecondary,
    } as any,
    listItemPressed: {
      opacity: 0.75,
    },
    listItemTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    listItemMeta: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    emptyLabel: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    detailsGrid: {
      gap: v(16),
    },
    detailItem: {
      gap: v(4),
    },
    detailItemFull: {
      gap: v(4),
    },
    detailLabel: {
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(14),
      color: theme.colors.textSecondary,
    },
    detailValue: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    detailLinkValue: {
      color: theme.colors.primary,
      textDecorationLine: 'underline',
    },
  });

export default BackOfficeSupplierDetailsScreen;
